import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  IconButton,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { ArrowBack as ArrowBackIcon } from "@mui/icons-material";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  detectKind,
  formatBytes,
  getExtension,
  humanKindLabel,
  PreviewKind,
} from "./previewTypes";
import { sniffKind } from "./sniffKind";
import { PreviewSource, previewSourceKey } from "./previewSource";
import { downloadBlob } from "./downloadBlob";
import { useBlobLoader } from "./useBlobLoader";
import PreviewActions from "./PreviewActions";
import PdfRenderer from "./renderers/PdfRenderer";
import ImageRenderer from "./renderers/ImageRenderer";
import VideoRenderer from "./renderers/VideoRenderer";
import AudioRenderer from "./renderers/AudioRenderer";
import TextRenderer from "./renderers/TextRenderer";
import JsonRenderer from "./renderers/JsonRenderer";
import HtmlRenderer from "./renderers/HtmlRenderer";
import DocxRenderer from "./renderers/DocxRenderer";
import XlsxRenderer from "./renderers/XlsxRenderer";
import CsvRenderer from "./renderers/CsvRenderer";
import ZipRenderer from "./renderers/ZipRenderer";
import FallbackRenderer from "./renderers/FallbackRenderer";

interface FilePreviewModalProps {
  open: boolean;
  source: PreviewSource;
  onClose: () => void;
  onCloseAll?: () => void;
  stackIndex: number;
  stackSize: number;
}

export interface RendererProps {
  blob: Blob | null;
  blobUrl: string;
  fileName: string;
  /** Size from the listing, known before the blob is fetched. */
  fileSize?: number;
  /** Stable identity of the file - see previewSourceKey. */
  sourceKey?: string;
  /** Depth in the preview stack; popovers need it to stay above their dialog. */
  stackIndex?: number;
  /**
   * Where the renderer may portal controls that belong in the modal header.
   * A spreadsheet puts its Export menu there so the header holds one download
   * entry point instead of two lookalike icons.
   */
  headerSlot?: HTMLElement | null;
  /** Downloads the original file, exactly as the header button does. */
  onDownloadOriginal?: () => void;
  /**
   * A renderer that offers its own download menu calls this with true, and the
   * header hides its own download icon for as long as the renderer is mounted.
   */
  claimHeaderDownload?: (claimed: boolean) => void;
  /**
   * Container for menus and popovers. Anything portalled to document.body is
   * invisible while the dialog is in browser fullscreen, so every popover this
   * renderer opens has to be anchored inside the dialog element.
   */
  getPopupContainer?: () => HTMLElement | null;
  onError?: (msg: string) => void;
}

const FilePreviewModal: React.FC<FilePreviewModalProps> = ({
  open,
  source,
  onClose,
  stackIndex,
  stackSize,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [fullscreen, setFullscreen] = useState(false);
  const [headerSlot, setHeaderSlot] = useState<HTMLElement | null>(null);
  const [rendererOwnsDownload, setRendererOwnsDownload] = useState(false);

  const fileName = useMemo(() => {
    return source.kind === "remote" ? source.item.fname : source.name;
  }, [source]);

  const fileSize = useMemo(() => {
    if (source.kind === "remote") return source.item.file_size;
    return source.size ?? source.blob.size;
  }, [source]);

  const ext = useMemo(() => getExtension(fileName), [fileName]);

  const presumedKind: PreviewKind = useMemo(
    () => detectKind(fileName, undefined),
    [fileName],
  );
  const useStreaming = presumedKind === "audio" || presumedKind === "video";

  const { loading, error, blob, url, retry } = useBlobLoader(
    open ? source : null,
    { stream: useStreaming },
  );

  const baseKind: PreviewKind = useMemo(() => {
    const mime = blob?.type;
    const detected = detectKind(fileName, mime);
    if (detected !== "unknown") return detected;
    return useStreaming ? presumedKind : detected;
  }, [fileName, blob, useStreaming, presumedKind]);

  const [sniffedKind, setSniffedKind] = useState<PreviewKind | null>(null);
  const [sniffing, setSniffing] = useState(false);

  useEffect(() => {
    setSniffedKind(null);
    if (useStreaming) {
      setSniffing(false);
      return;
    }
    if (!blob) {
      setSniffing(false);
      return;
    }
    if (baseKind !== "unknown") {
      setSniffing(false);
      return;
    }
    let cancelled = false;
    setSniffing(true);
    void sniffKind(blob)
      .then((detected) => {
        if (cancelled) return;
        setSniffedKind(detected);
      })
      .catch(() => {
        if (cancelled) return;
        setSniffedKind("unknown");
      })
      .finally(() => {
        if (cancelled) return;
        setSniffing(false);
      });
    return () => {
      cancelled = true;
    };
  }, [blob, baseKind, useStreaming]);

  const kind: PreviewKind =
    baseKind !== "unknown" ? baseKind : (sniffedKind ?? "unknown");

  const handleDownload = useCallback(async () => {
    if (blob) {
      downloadBlob(blob, fileName);
      return;
    }
    if (!url) return;
    try {
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`Failed to download (status ${response.status})`);
      }
      downloadBlob(await response.blob(), fileName);
    } catch {
      window.open(url, "_blank", "noopener,noreferrer");
    }
  }, [blob, url, fileName]);

  const handleToggleFullscreen = useCallback(() => {
    const node = containerRef.current;
    if (!node) return;
    const fsEl = document.fullscreenElement;
    if (!fsEl) {
      node.requestFullscreen?.().catch(() => undefined);
    } else {
      document.exitFullscreen?.().catch(() => undefined);
    }
  }, []);

  useEffect(() => {
    const onChange = () => setFullscreen(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", onChange);
    return () => document.removeEventListener("fullscreenchange", onChange);
  }, []);

  const onBack = source.kind === "inline" ? source.onBack : undefined;
  const backLabel =
    source.kind === "inline" ? (source.backLabel ?? "Back") : undefined;

  const renderBody = () => {
    if (loading || sniffing) {
      return (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            py: 6,
            gap: 2,
            minHeight: 240,
          }}
        >
          <CircularProgress />
          <Typography variant="body2" color="text.secondary">
            {sniffing ? "Detecting file type…" : "Loading preview…"}
          </Typography>
        </Box>
      );
    }
    if (error) {
      return (
        <Box sx={{ p: 3 }}>
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
          <Stack direction="row" spacing={1}>
            <Button variant="contained" size="small" onClick={retry}>
              Retry
            </Button>
            <Button
              variant="outlined"
              size="small"
              onClick={handleDownload}
              disabled={!blob && !url}
            >
              Download
            </Button>
          </Stack>
        </Box>
      );
    }
    if (!url) return null;
    if (!useStreaming && !blob) return null;

    const props: RendererProps = {
      blob,
      blobUrl: url,
      fileName,
      fileSize: fileSize ?? undefined,
      sourceKey: previewSourceKey(source),
      stackIndex,
      headerSlot,
      onDownloadOriginal: handleDownload,
      claimHeaderDownload: setRendererOwnsDownload,
      getPopupContainer: () =>
        document.fullscreenElement instanceof HTMLElement
          ? document.fullscreenElement
          : containerRef.current,
    };

    switch (kind) {
      case "pdf":
        return <PdfRenderer {...props} />;
      case "image":
        return <ImageRenderer {...props} />;
      case "video":
        return <VideoRenderer {...props} />;
      case "text":
        return <TextRenderer {...props} />;
      case "csv":
        return <CsvRenderer {...props} />;
      case "json":
        return <JsonRenderer {...props} />;
      case "html":
        return <HtmlRenderer {...props} />;
      case "docx":
        return <DocxRenderer {...props} />;
      case "xlsx":
      case "xls":
        return <XlsxRenderer {...props} />;
      case "zip":
        return <ZipRenderer {...props} />;
      case "doc":
        return (
          <FallbackRenderer
            {...props}
            onDownload={handleDownload}
            reason={`Legacy ${ext.toUpperCase()} format cannot be previewed in the browser. Please download to view.`}
          />
        );
      case "unknown":
      default:
        return (
          <FallbackRenderer
            {...props}
            onDownload={handleDownload}
            reason="Preview is not available for this file type."
          />
        );
    }
  };

  const isAudioMode = kind === "audio";

  const paperBaseSx = {
    display: "flex",
    flexDirection: "column" as const,
    overflow: "hidden",
  };

  const paperSx = isAudioMode
    ? {
        ...paperBaseSx,
        width: { xs: "100%", sm: "min(560px, calc(100vw - 64px))" },
        height: { xs: "auto", sm: "auto" },
        maxWidth: { xs: "100%", sm: "calc(100vw - 64px)" },
        maxHeight: { xs: "100%", sm: "calc(100vh - 140px)" },
        borderRadius: { xs: 0, sm: 1 },
      }
    : {
        ...paperBaseSx,
        width: { xs: "100%", sm: "min(1100px, calc(100vw - 64px))" },
        height: {
          xs: "100%",
          sm: fullscreen ? "100%" : "min(820px, calc(100vh - 140px))",
        },
        maxWidth: { xs: "100%", sm: "calc(100vw - 64px)" },
        maxHeight: {
          xs: "100%",
          sm: fullscreen ? "100%" : "calc(100vh - 140px)",
        },
        borderRadius: { xs: 0, sm: fullscreen ? 0 : 1 },
      };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={!isAudioMode && (isMobile || fullscreen)}
      maxWidth={isAudioMode ? "sm" : "md"}
      fullWidth
      PaperProps={{
        ref: (node: HTMLDivElement | null) => {
          containerRef.current = node;
        },
        elevation: 8,
        sx: paperSx,
      }}
      sx={{
        zIndex: (t) => t.zIndex.modal + stackIndex,
        "& .MuiDialog-container": {
          alignItems: { xs: "center", sm: "center" },
        },
      }}
    >
      {isAudioMode ? (
        <AudioRenderer
          blobUrl={url}
          fileName={fileName}
          fileSize={fileSize ?? undefined}
          loading={loading}
          error={error}
          onDownload={handleDownload}
          onClose={onClose}
          onRetry={retry}
          onBack={onBack}
          backLabel={backLabel}
          disableDownload={!blob && !url}
        />
      ) : (
        <>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              px: { xs: 2, sm: 3 },
              py: { xs: 1.25, sm: 1.5 },
              borderBottom: "1px solid",
              borderColor: "divider",
              flexShrink: 0,
            }}
          >
            {onBack && (
              <IconButton
                size="small"
                onClick={onBack}
                aria-label={backLabel || "Back"}
              >
                <ArrowBackIcon fontSize="small" />
              </IconButton>
            )}
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 700,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
                title={fileName}
              >
                {fileName}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {humanKindLabel(kind, ext)}
                {fileSize != null && fileSize > 0
                  ? ` • ${formatBytes(fileSize)}`
                  : ""}
                {stackSize > 1 ? ` • Layer ${stackIndex + 1}/${stackSize}` : ""}
              </Typography>
            </Box>
            <Box
              ref={(node: HTMLDivElement | null) => setHeaderSlot(node)}
              sx={{ display: "flex", alignItems: "center", minWidth: 0 }}
            />
            <PreviewActions
              fileName={fileName}
              onDownload={handleDownload}
              onToggleFullscreen={handleToggleFullscreen}
              isFullscreen={fullscreen}
              onClose={onClose}
              disableDownload={!blob && !url}
              hideDownload={rendererOwnsDownload}
            />
          </Box>
          <Box
            sx={{
              flex: "1 1 0",
              minHeight: 0,
              minWidth: 0,
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
              backgroundColor: (t) => t.palette.grey[50],
            }}
          >
            {renderBody()}
          </Box>
        </>
      )}
    </Dialog>
  );
};

export default FilePreviewModal;
